import React from 'react';

import { useDispatch } from 'react-redux';

import Button from 'components/Button';

import { useAuth } from 'hooks/useAuth';
import useReactivateTrialSubscription from 'hooks/useReactivateTrialSubscription';
import { useTracking } from 'hooks/useTracking';

import { openSubscriptionDetailsPortal } from 'store/modules/chargebee';

interface IProps {
  applyCoupon?: boolean;
  smallButton?: boolean;
  trialCtaTranslationKey?: string;
}

const useOpenPortal = () => {
  const dispatch = useDispatch();
  const { trackContentInteraction } = useTracking();

  const openPortal = React.useCallback(() => {
    trackContentInteraction('click', 'chargebee', 'reactivate-subscription');
    dispatch(openSubscriptionDetailsPortal());
  }, [dispatch, trackContentInteraction]);

  return { openPortal };
};

const ReactivateSubscription = ({
  applyCoupon = false,
  smallButton = false,
  trialCtaTranslationKey = 'components.ReactivateSubscription.cta',
}: IProps) => {
  const { openPortal } = useOpenPortal();
  const { activeSubscription } = useAuth();

  const {
    canReactivateTrialSubscription,
    reactivateTrialSubscription,
    reactivateTrialSubscriptionLoading,
  } = useReactivateTrialSubscription({
    applyCoupon,
    trackingName: 'reactivate-button',
  });

  if (!activeSubscription || activeSubscription.bespoke) return null;

  if (canReactivateTrialSubscription) {
    return (
      <Button
        className="ReactivateSubscription"
        data-testid="reactivate-subscription-link-reactivate"
        small={smallButton}
        title={{
          id: trialCtaTranslationKey,
        }}
        onClick={reactivateTrialSubscription}
        loading={reactivateTrialSubscriptionLoading}
      />
    );
  }

  if (
    activeSubscription.cancellationScheduledAt ||
    new Date(activeSubscription.expiresAt) < new Date() // Do we want to keep this?
  ) {
    return (
      <Button
        className="ReactivateSubscription"
        data-testid="reactivate-subscription-link-open-portal"
        small={smallButton}
        title={{ id: 'components.ReactivateSubscription.cta' }}
        onClick={openPortal}
      />
    );
  }

  return null;
};

export default ReactivateSubscription;
